/**
 * Loyalty platform theme presets.
 *
 * Each preset defines a complete set of visual tokens that transform
 * the website's look & feel — from a clean corporate style to
 * an aggressive brutalist gaming aesthetic.
 */

export type ThemePreset = 'clean' | 'aggressive' | 'sport' | 'custom';

export interface ThemePresetValues {
  preset: ThemePreset;
  // Colors
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  surfaceBorderColor: string;
  textColor: string;
  textMutedColor: string;
  // Typography
  fontFamily: string;
  fontFamilyHeading: string;
  headingWeight: string;
  headingStyle: string;
  headingTransform: string;
  letterSpacing: string;
  // Shape & Effects
  borderRadius: string;
  shadowStyle: string;
  glowEnabled: boolean;
  glowColor: string;
  skewEnabled: boolean;
  patternBackground: string;
}

export const PRESET_CLEAN: ThemePresetValues = {
  preset: 'clean',
  primaryColor: '#067ff9',
  secondaryColor: '#0f1923',
  accentColor: '#3b82f6',
  backgroundColor: '#0f1923',
  surfaceColor: '#1e293b',
  surfaceBorderColor: '#334155',
  textColor: '#f1f5f9',
  textMutedColor: '#94a3b8',
  fontFamily: 'Inter',
  fontFamilyHeading: 'Inter',
  headingWeight: 'bold',
  headingStyle: 'normal',
  headingTransform: 'none',
  letterSpacing: 'normal',
  borderRadius: 'lg',
  shadowStyle: 'subtle',
  glowEnabled: false,
  glowColor: '#067ff9',
  skewEnabled: false,
  patternBackground: 'none',
};

export const PRESET_AGGRESSIVE: ThemePresetValues = {
  preset: 'aggressive',
  primaryColor: '#00f0ff',
  secondaryColor: '#0a111f',
  accentColor: '#00f0ff',
  backgroundColor: '#0a111f',
  surfaceColor: '#121d2f',
  surfaceBorderColor: '#1e293b',
  textColor: '#f1f5f9',
  textMutedColor: '#64748b',
  fontFamily: 'Inter',
  fontFamilyHeading: 'Inter',
  headingWeight: 'black',
  headingStyle: 'italic',
  headingTransform: 'uppercase',
  letterSpacing: 'wide',
  borderRadius: 'sm',
  shadowStyle: 'brutalist',
  glowEnabled: true,
  glowColor: '#00f0ff',
  skewEnabled: true,
  patternBackground: 'carbon',
};

export const PRESET_SPORT: ThemePresetValues = {
  preset: 'sport',
  primaryColor: '#ff9900',
  secondaryColor: '#0a1628',
  accentColor: '#ff9900',
  backgroundColor: '#0a1628',
  surfaceColor: '#111d33',
  surfaceBorderColor: '#1e3050',
  textColor: '#f1f5f9',
  textMutedColor: '#64748b',
  fontFamily: 'Inter',
  fontFamilyHeading: 'Inter',
  headingWeight: 'black',
  headingStyle: 'italic',
  headingTransform: 'uppercase',
  letterSpacing: 'wide',
  borderRadius: 'sm',
  shadowStyle: 'brutalist',
  glowEnabled: true,
  glowColor: '#ff9900',
  skewEnabled: true,
  patternBackground: 'carbon',
};

export const THEME_PRESETS: Record<string, ThemePresetValues> = {
  clean: PRESET_CLEAN,
  aggressive: PRESET_AGGRESSIVE,
  sport: PRESET_SPORT,
};

/** CSS variable mappings for theme tokens that go beyond the existing branding vars */
export const HEADING_WEIGHT_MAP: Record<string, string> = {
  semibold: '600',
  bold: '700',
  extrabold: '800',
  black: '900',
};

export const LETTER_SPACING_MAP: Record<string, string> = {
  normal: '0',
  wide: '0.05em',
  wider: '0.1em',
};

export const SHADOW_STYLE_MAP: Record<string, string> = {
  none: 'none',
  subtle: '0 1px 3px rgba(0,0,0,0.3)',
  medium: '0 4px 6px rgba(0,0,0,0.4)',
  brutalist: '4px 4px 0 rgba(0,0,0,0.8)',
};
